import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { fmtEur } from "@/lib/format";
import { exportElementToPdf, exportElementsToPdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ProjectionCharts } from "@/components/BPCharts";
import { AnalysisPrintable } from "@/components/AnalysisPrintable";
import {
  ArrowLeft, Download, Printer, RefreshCw, Sparkles, TrendingUp, Target,
  Percent, Timer, Wallet, BrainCircuit,
} from "lucide-react";

const KPI = (icon, label, value, sub, color) => ({ icon, label, value, sub, color });

export default function BusinessPlanReport() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [bp, setBp] = useState(null);
  const [linkedAnalysis, setLinkedAnalysis] = useState(null);
  const [regen, setRegen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const ref = useRef(null);
  const analysisRef = useRef(null);

  useEffect(() => {
    api.get(`/business-plans/${id}`).then((r) => {
      setBp(r.data);
      if (r.data.linked_analysis_id) {
        api.get(`/analyses/${r.data.linked_analysis_id}`)
          .then((a) => setLinkedAnalysis(a.data)).catch(() => {});
      }
    }).catch(() => {
      toast.error(lang === "it" ? "Business Plan non trovato" : "Business Plan not found");
      navigate("/dashboard");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!bp || bp.narrative_status !== "generating") return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/business-plans/${id}`);
        if (data.narrative_status !== "generating") setBp(data);
      } catch (e) { console.debug("BP narrative poll retry:", e); }
    }, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bp?.narrative_status, id]);

  const regenerate = async () => {
    setRegen(true);
    try {
      await api.post(`/business-plans/${id}/narrative`);
      setBp((p) => ({ ...p, narrative_status: "generating" }));
    } catch { toast.error("Error"); } finally { setRegen(false); }
  };

  const downloadPdf = async () => {
    if (!ref.current) return;
    setPdfLoading(true);
    try {
      const safe = (bp?.company_name || "business-plan").replace(/[^\w-]+/g, "_").slice(0, 40);
      if (linkedAnalysis && analysisRef.current) {
        // Combined document: analysis summary first, then the full business plan
        await exportElementsToPdf([analysisRef.current, ref.current], `Rexxpons_Analisi_BP_${safe}.pdf`);
      } else {
        await exportElementToPdf(ref.current, `Rexxpons_BP_${safe}.pdf`);
      }
    } catch { toast.error(lang === "it" ? "Errore PDF" : "PDF error"); } finally { setPdfLoading(false); }
  };

  if (!bp) return (
    <div className="App bg-slate-950 min-h-screen"><Navbar />
      <div className="h-[60vh] flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-slate-800 border-t-blue-600 animate-spin" />
      </div>
    </div>
  );

  const r = bp.result || {};
  const k = r.kpi || {};
  const proj = r.projections || [];
  const sc = r.scenarios || {};
  const n = bp.narrative || {};
  const generating = bp.narrative_status === "generating";

  const kpis = [
    KPI(Target, "NPV (VAN)", fmtEur(k.npv), lang === "it" ? "Valore Attuale Netto" : "Net Present Value", k.npv >= 0 ? "text-emerald-400" : "text-red-400"),
    KPI(Percent, "IRR (TIR)", k.irr != null ? `${k.irr}%` : "n/d", lang === "it" ? "Tasso Interno di Rendimento" : "Internal Rate of Return", "text-blue-400"),
    KPI(Timer, "Break-even", k.break_even_months != null ? `${k.break_even_months} ${lang === "it" ? "mesi" : "months"}` : "n/d", lang === "it" ? "Punto di pareggio" : "Payback", "text-cyan-400"),
    KPI(Wallet, lang === "it" ? "Utile cumulato" : "Cumulative profit", fmtEur(k.cumulative_net_income), `${proj.length} ${lang === "it" ? "anni" : "years"}`, "text-white"),
  ];

  const scRows = [
    ["pessimistic", lang === "it" ? "Pessimistico" : "Pessimistic", "#EF4444"],
    ["base", lang === "it" ? "Base" : "Base", "#3B82F6"],
    ["optimistic", lang === "it" ? "Ottimistico" : "Optimistic", "#10B981"],
  ];

  const sections = [
    ["executive_summary", lang === "it" ? "Sintesi Esecutiva" : "Executive Summary"],
    ["market_analysis", lang === "it" ? "Analisi di Mercato" : "Market Analysis"],
    ["operations", "Operations"],
    ["strategy", lang === "it" ? "Strategia" : "Strategy"],
    ["financial_highlights", lang === "it" ? "Highlights Finanziari" : "Financial Highlights"],
  ];

  return (
    <div className="App bg-slate-950 min-h-screen">
      <Navbar />
      <main ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="bp-report">
        <div className="no-print flex items-center justify-between gap-3 mb-6 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="bp-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> {t("back")}
          </Button>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={regenerate} disabled={regen} data-testid="bp-regen-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${regen ? "animate-spin" : ""}`} /> {t("regenerate")}
            </Button>
            <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfLoading} data-testid="bp-pdf-btn">
              <Download className={`h-4 w-4 mr-1.5 ${pdfLoading ? "animate-pulse" : ""}`} /> {pdfLoading ? t("pdf_generating") : (linkedAnalysis ? (lang === "it" ? "PDF completo" : "Full PDF") : t("pdf"))}
            </Button>
            <Button size="sm" onClick={() => window.print()} data-testid="bp-print-btn">
              <Printer className="h-4 w-4 mr-1.5" /> {t("print")}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold"><TrendingUp className="h-4 w-4" /> Business Plan</div>
        <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-white">{bp.company_name}</h1>
        <p className="text-slate-400 mt-1">{bp.mode === "startup" ? (lang === "it" ? "Modalità startup" : "Startup mode") : (lang === "it" ? "Da analisi bilancio" : "From analysis")}
          {bp.sector ? ` · ${bp.sector}` : ""}</p>

        {/* KPI */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((kp, i) => (
            <div key={kp.label} className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={`bp-kpi-${i}`}>
              <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider">
                <kp.icon className="h-4 w-4" /> {kp.label}
              </div>
              <p className={`mt-2 font-mono-fin text-2xl font-bold ${kp.color}`}>{kp.value}</p>
              <p className="text-xs text-slate-500 mt-1">{kp.sub}</p>
            </div>
          ))}
        </div>

        {/* Projections */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4">{lang === "it" ? "Proiezioni economiche" : "Financial projections"}</h2>
        <ProjectionCharts projections={proj} t={t} lang={lang} />

        {/* Scenarios */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4">{lang === "it" ? "Scenari What-If" : "What-If scenarios"}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {scRows.map(([key, label, color]) => {
            const s = sc[key] || {};
            return (
              <div key={key} className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={`bp-scenario-${key}`}>
                <p className="text-sm font-semibold" style={{ color }}>{label}</p>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">NPV</span><span className="font-mono-fin text-white">{fmtEur(s.npv)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">IRR</span><span className="font-mono-fin text-white">{s.irr != null ? `${s.irr}%` : "n/d"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Break-even</span><span className="font-mono-fin text-white">{s.break_even_months != null ? `${s.break_even_months} ${lang === "it" ? "mesi" : "m"}` : "n/d"}</span></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* P&L projection table */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4">{lang === "it" ? "Conto Economico previsionale" : "Projected P&L"}</h2>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 overflow-x-auto" data-testid="bp-pl-table">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-slate-400 text-left">
                <th className="py-2 pr-4 font-medium">{lang === "it" ? "Voce" : "Item"}</th>
                {proj.map((p) => <th key={p.year} className="py-2 px-3 text-right font-medium">{lang === "it" ? "Anno" : "Y"} {p.year}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {[["revenue", lang === "it" ? "Ricavi" : "Revenue"],
                ["ebitda", "EBITDA"],
                ["ebit", "EBIT"],
                ["net_income", lang === "it" ? "Utile netto" : "Net income"],
                ["fcf", "Free Cash Flow"]].map(([key, label]) => (
                <tr key={key}>
                  <td className="py-2 pr-4 text-slate-300">{label}</td>
                  {proj.map((p) => <td key={p.year} className="py-2 px-3 text-right font-mono-fin text-white">{fmtEur(p[key])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* AI narrative */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
          <BrainCircuit className="h-5 w-5 text-blue-400" /> {lang === "it" ? "Piano Narrativo (AI)" : "Narrative Plan (AI)"}
        </h2>
        {generating ? (
          <div className="rounded-xl border border-blue-900 bg-blue-950/30 p-6 flex items-center gap-3" data-testid="bp-narrative-generating">
            <div className="h-5 w-5 rounded-full border-2 border-blue-900 border-t-blue-400 animate-spin" />
            <span className="text-blue-200 text-sm">{lang === "it" ? "Generazione del piano narrativo AI in corso…" : "Generating the AI narrative plan…"}</span>
          </div>
        ) : (
          <div className="space-y-5" data-testid="bp-narrative">
            {sections.map(([key, label]) => n[key] ? (
              <div key={key} className="rounded-xl border border-slate-800 bg-slate-900 p-6">
                <h3 className="font-heading text-base font-semibold text-white mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" /> {label}</h3>
                <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{n[key]}</p>
              </div>
            ) : null)}
            {Array.isArray(n.risks) && n.risks.length > 0 && (
              <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
                <h3 className="font-heading text-base font-semibold text-white mb-3">{lang === "it" ? "Rischi & Mitigazione" : "Risks & Mitigation"}</h3>
                <ul className="space-y-2">
                  {n.risks.map((rk) => (
                    <li key={`risk-${rk}`} className="text-slate-300 text-sm flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" /> {rk}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Off-screen printable analysis summary for the combined PDF */}
      {linkedAnalysis && (
        <div aria-hidden="true" style={{ position: "absolute", left: -99999, top: 0, width: 980 }}>
          <div ref={analysisRef}>
            <AnalysisPrintable analysis={linkedAnalysis} lang={lang} />
          </div>
        </div>
      )}
    </div>
  );
}
